<template>
  <div>
    <div class="mb-6 pb-6 border-b-2 border-grey-light flex flex-wrap items-center">
      <div>
        <h1 class="text-grey-darker text-xl font-semibold leading-normal uppercase">
          Custom Print Order - Details
        </h1>
        <a
          @click="$router.push('/print-order-form/order-history')"
          class="text-grey-dark text-sm font-medium leading-normal cursor-pointer no-underline hover:underline">
          &lt; Back to Order History
        </a>
      </div>
    </div>

    <loader v-if="!loaded || !orderItem" />

    <!-- OFFICE HAS NO ACCESS -->
    <panel v-else-if="!officeAccess" class="pt-6">
      <strong class="mb-2">Access Denied</strong>
      <p>Your office does not currently have access to use the print order form.</p>
    </panel>
    <panel v-else-if="!checkOrderPermission" class="pt-6">
      <strong class="mb-2">Access Denied</strong>
      <p>This order was placed under {{ orderItem.office.name }}. To view this order, change office by clicking on the cog in the top right corner of your page > Change Office.</p>
    </panel>
    <div v-else class="rounded-lg bg-white relative flex flex-col p-8">
      <div class="flex flex-wrap">
        <div class="mb-6 w-full lg:w-2/3">
          <p class="mb-6 text-grey-dark">Part of order {{ orderItem.order.id }} ({{ orderItem.order_item_count }} {{ orderItem.order_item_count === 1 ? 'Item' : 'Items' }} - {{ orderItem.order.cost_total | gbp }})</p>
          <h3 class="mb-2 font-semibold">Order {{ orderItem.order.id }}</h3>
          <p class="mb-2 text-grey-dark">Ordered: <span class="italic">{{ orderItem.created_at | date }}</span></p>
          <p v-if="orderItem.deleted_at" class="mb-2 text-red-darker">Cancelled: <span class="italic">{{ orderItem.deleted_at | date }}</span></p>
          <p v-if="orderItem.customer_reference" class="mb-2 text-grey-dark">Reference: <span class="italic">{{ orderItem.customer_reference }}</span></p>
          <p class="text-grey-darker font-semibold" :class="hasAdditionalContacts ? 'mb-2' : 'mb-8'">Placed by: {{ orderItem.user.email }}</p>
          <p v-if="hasAdditionalContacts" class="mb-8 text-grey-darker font-semibold">Additional contacts: {{ orderItem.combined_notification_cc.join(', ') }}</p>
          <h3 class="mb-6 pb-4 border-b font-semibold">Item</h3>
          <table class="w-full mb-8 leading-normal">
            <tbody>
              <tr class="align-top">
                <td class="pr-4">
                  <h4 class="mb-1 font-semibold">{{ orderItem.sale_item_category.name + ' - ' + orderItem.sale_item.name }}</h4>
                  <p class="mb-4 text-sm font-semibold">Quantity: {{ orderItem.quantity }}</p>
                  <p v-if="orderItem.print_spec" class="mb-4 text-sm">{{ orderItem.print_spec.name }}</p>
                  <p v-if="orderItem.artwork_type_id" class="mb-4 text-sm font-semibold">Artwork: {{ artworkType }}</p>
                  <div v-if="orderItem.edition_number && orderItem.delivery_date">
                    <p class="mb-2 text-sm">Magazine edition: {{ orderItem.edition_number }}</p>
                    <p class="mb-6 text-sm">Estimated delivery date: {{ orderItem.delivery_date }}</p>
                  </div>
                  <table v-if="customFields && customFields.length > 0" class="text-sm mb-4">
                    <tbody>
                      <PrintOrderCustomField v-for="customField in customFields" :key="customField.id" :customField="customField" :orderItem="orderItem" />
                    </tbody>
                  </table>
                </td>
                <td class="pr-4 text-right">
                  <p class="mb-4">
                    <span :class="{'pr-2 line-through text-grey-dark': orderItem.item_discount}">{{ orderItem.item_cost | gbp }}</span>
                    <span v-if="orderItem.item_discount">{{ orderItem.calculated_item_cost | gbp }}</span>
                  </p>
                </td>
                <td class="">
                  <loader v-if="reordering" />
                  <i v-else @click="reOrder(orderItem)" class="tooltip-container fas fa-cart-plus text-xl text-blue cursor-pointer">
                    <div class="absolute border border-bg-primary p-2 bg-grey-darker shadow z-10 rounded text-xs normal-case mt-1 tooltip-msg pointer-events-none">
                      <span class="text-white font-sans leading-normal font-normal whitespace-normal">
                        Re-order this item.<br />
                        Add it to my basket
                      </span>
                    </div>
                  </i>
                </td>
              </tr>
              <tr v-if="orderItem.artwork_cost" class="align-top">
                <td class="pr-4">
                  <h4 class="mb-4 font-semibold">Artwork Creation</h4>
                  <p v-if="orderItem.notes" class="mb-4 text-sm">Notes: {{ orderItem.notes }}</p>
                  <p v-if="attachmentsFileList" class="mb-4 text-sm">Uploads: {{ attachmentsFileList }}</p>
                </td>
                <td class="pr-4 text-right">
                  <p class="mb-4">
                    <span :class="{'pr-2 line-through text-grey-dark': orderItem.artwork_discount}">{{ orderItem.artwork_cost | gbp }}</span>
                    <span v-if="orderItem.artwork_discount">{{ orderItem.calculated_artwork_cost | gbp }}</span>
                  </p>
                </td>
                <td class=""></td>
              </tr>
              <tr v-if="orderItem.delivery_cost" class="align-top">
                <td class="pr-4">
                  <h4 class="mb-4 font-semibold">Delivery</h4>
                </td>
                <td class="pr-4 text-right">
                  <p class="mb-4">
                    <span :class="{'pr-2 line-through text-grey-dark': orderItem.delivery_discount}">{{ orderItem.delivery_cost | gbp }}</span>
                    <span v-if="orderItem.delivery_discount">{{ orderItem.calculated_delivery_cost | gbp }}</span>
                  </p>
                </td>
                <td class=""></td>
              </tr>
            </tbody>
          </table>

          <p v-if="!requiresProof" class="mb-4 text-sm">Proofing is not required for this item.</p>

          <table class="w-full mb-8 leading-normal">
            <thead>
              <tr class="text-xs uppercase leading-tight">
                <th class="text-left font-semibold pl-6 pr-4 py-4">Fulfilment</th>
                <th class="text-center font-semibold p-4">Scheduled</th>
                <th class="text-center font-semibold p-4">Completed</th>
                <th class="text-center font-semibold p-4">Status</th>
                <th class="text-center font-semibold p-4">Proof</th>
                <th class="text-center font-semibold pl-4 pr-6 py-4">Approve</th>
              </tr>
            </thead>
            <tbody>
              <tr class="border-t border-b border-grey-light text-sm">
                <td class="pl-6 pr-4 py-4 border-l-8 border-grey">Studio</td>
                <td class="text-center p-4">
                  <span v-if="orderItem.scheduled_at">
                    {{ orderItem.scheduled_at | datetime(false) }}
                    <small class="text-grey-darker block">{{ orderItem.scheduled_at | time }}</small>
                  </span>
                  <span v-else>-</span>
                </td>
                <td class="text-center p-4">
                  <span v-if="orderItem.completed_at">
                    {{ orderItem.completed_at | date }}
                    <small class="text-grey-darker block">{{ orderItem.completed_at | time }}</small>
                  </span>
                  <span v-else>-</span>
                </td>
                <td class="text-center p-4">
                  <span v-if="orderItem.is_stock_item">
                    <i class="fas fa-check text-green-dark" title="Completed"></i>
                  </span>
                  <span v-else>
                    <i class="fas fa-hourglass-half" v-if="orderItem.scheduled_at === null" title="Needs Scheduling"></i>
                    <i class="far fa-clock text-red-dark" v-else-if="orderItem.completed_at === null" title="Awaiting Fulfillment"></i>
                    <i class="fas fa-check text-green-dark" v-else title="Completed"></i>
                  </span>
                </td>
                <td class="text-center p-4">
                  <i v-if="orderItem.proof && orderItem.proof.url" class="fas fa-download text-xl text-blue cursor-pointer" @click="openUrl(orderItem.proof.url)" />
                  <span v-else>-</span>
                </td>
                <td class="text-center pl-4 pr-6 py-4 border-r-8 border-grey">
                  <span v-if="!orderItem.proof">-</span>
                  <button v-else-if="!orderItem.approved_at" @click="approvingArtwork = true" class="rounded bg-green-dark hover:bg-green text-white p-2 whitespace-no-wrap">
                    Approve
                  </button>
                  <button v-else class="rounded bg-green-dark text-white p-2 whitespace-no-wrap cursor-default">
                    Approved
                  </button>
                </td>
              </tr>
            </tbody>
          </table>

          <h4 class="mt-12 pt-8 mb-4 border-t font-semibold">File Uploads</h4>
          <div id="cpo-dropzone" class="w-full flex flex-row mb-6 p-6 border border-dashed rounded-lg">
            <div class="w-full md:w-2/3 md:mr-4 md:pr-4 md:border-r border-dashed border-grey">
              <table v-if="(sourceMaterial && sourceMaterial.length > 0) || (orderItem.uploads && orderItem.uploads.length > 0)">
                <tbody>
                  <tr v-for="(file, i) in sourceMaterial" :key="i" class="h-12">
                    <td class="inline-block w-12 h-12 mr-2 my-1">
                      <img v-if="file.thumb" :src="file.thumb" :alt="file.filename" loading="lazy" class="block w-12 h-12 bg-grey-lighter object-contain" />
                      <div v-else class="block w-12 h-12 bg-grey-lighter"></div>
                    </td>
                    <td class="w-full text-sm">
                      {{ file.filename }}
                      <a @click="deleteAsset(orderItem, file)" class="text-blue text-sm font-medium leading-normal cursor-pointer underline hover:no-underline">Delete</a>
                    </td>
                    <td>
                      <i class="fas fa-check-circle text-print-order-teal fa-2x pl-2"></i>
                    </td>
                  </tr>
                  <tr v-for="(file, i) in orderItem.uploads" :key="100 + i" class="h-12">
                    <td class="inline-block w-12 h-12 mr-2 my-1">
                      <div class="block w-12 min-h-8 h-full bg-grey-lighter object-contain"></div>
                    </td>
                    <td class="w-full text-sm">{{ file.filename }}</td>
                    <td>
                      <i v-if="file.complete" class="fas fa-check-circle text-print-order-teal fa-2x pl-2"></i>
                      <gauge-graph v-else :series="[{ color: '#eee', value: 101 },{ color: '#0d9eff', value: file.progress }]" :maximum="101" :arc-size="360" :offset="0" class="w-8 h-8">
                      </gauge-graph>
                    </td>
                  </tr>
                </tbody>
              </table>
              <p v-else class="mb-6">There are no file uploads on this order.</p>
            </div>
            <upload-dropzone @change="uploadFiles($event)" accept="png" containerClass="w-full md:w-1/3" />
          </div>

          <h4 class="mt-12 pt-8 mb-4 border-t font-semibold">Contact Us</h4>
          <loader v-if="notes === null" />
          <div v-else class="w-full mb-6">
            <div class="w-full mb-6 border-b">
              <p class="mb-3 leading-normal">For anything related to the artwork section of your order, please contact the Studio team. For all other queries, please contact the Support team.</p>
              <textarea v-model="internalNoteForm.body" class="w-full border hover:border-grey-darker focus:border-grey-darkest p-3" rows="6"></textarea>
              <div class="flex flex-wrap mt-3 mb-6">
                <div class="w-full md:w-2/3">
                  <p class="mb-3 text-sm leading-normal">* 'Internal Note' unless tick boxes are selected below</p>
                  <div class="flex">
                    <div>
                      <input type="checkbox" id="note_studio" v-model="internalNoteForm.show_studio" />
                      <label for="note_studio" class="text-sm mr-12 font-bold">Send to Studio team</label>
                    </div>
                    <div>
                      <input type="checkbox" id="note_show_admin" v-model="internalNoteForm.show_admin" />
                      <label for="note_show_admin" class="text-sm font-bold">Send to Support team</label>
                    </div>
                  </div>
                </div>

                <div class="w-full md:w-1/3 mt-4 text-right">
                  <button type="button" class="py-2 px-6 bg-blue text-white text-sm leading-normal rounded shadow" @click="addNote">
                    <i class="fas fa-sync fa-fw fa-spin" v-if="savingInternalNote"></i>
                    <span v-else>Send</span>
                  </button>
                </div>
              </div>
            </div>
            <p v-if="notes.length === 0" class="w-full">
              There are no notes on this order.
            </p>
            <div v-else class="w-full">
              <p class="mb-3">Notes on this item:</p>
              <div v-for="note in notes" :key="note.id" class="border p-3 mb-3 text-sm" :class="{'bg-yellow-lighter': checkIfPrivateNote(note)}">
                <div class="flex -mx-2">
                  <div class="w-20 px-2">
                    <img v-if="note.user.small_photo_url" :src="note.user.small_photo_url" loading="lazy" class="w-16 h-16 rounded-full object-cover" />
                    <div v-else>
                      <div class="w-16 h-16 rounded-full flex items-center justify-center bg-grey-light text-grey-dark text-lg">
                        <i class="fas fa-user"></i>
                      </div>
                    </div>
                  </div>
                  <div class="flex-1 px-2">
                    <div class="flex -mx-2">
                      <div class="flex-1 px-2">
                        <strong>{{ note.user.first_name }} {{ note.user.surname }}</strong>
                        <span class="bg-blue text-white rounded-full px-2 text-xs ml-2" v-if="[1,2].includes(note.user.type)">ADMIN</span>
                        <em class="block mt-1 mb-3">{{ note.created_at | datetime }}<span v-if="note.private"> - Private note</span></em>
                      </div>
                      <div class="px-2" v-if="note.user_id === profile.id">
                        <i class="fas fa-trash-alt cursor-pointer hover:text-red-dark" title="Delete Note" @click="deleteNote(note)"></i>
                      </div>
                    </div>
                    <p class="whitespace-pre-line">{{ note.body }}</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div class="w-full lg:w-1/3 lg:pl-6">
          <PrintOrderItemSummary :orderItem="orderItem" />
          <PrintOrderDelivery @updateAddress="updateDeliveryAddress" :deliveryAddressId="deliveryAddressId" :deliveryAddress="salesOrderDeliveryAddress" :deliveryAddresses="deliveryAddresses" :requiresDeliveryAddress="orderItem.requires_delivery_address" :editable="deliveryEditable" :updatingItem="updatingItem" />
          <div v-if="orderItem.order.phone_number" class="border border-grey-light mb-8">
            <div class="w-full flex text-sm p-4 bg-grey-lighter border-t-2 border-print-order-teal">
              <p  class="mb-4 text-sm font-semibold">Phone Number: {{ orderItem.order.phone_number }}</p>
            </div>
          </div>
        </div>
      </div>
    </div>

    <deletion-modal
      v-if="approvingArtwork"
      @confirmed="approveArtwork()"
      @leave="approvingArtwork = false"
      @dismissed="approvingArtwork = false"
      :loading="sendingArtworkApproval"
      confirm-label="Yes, approve it.">
      <p class="mb-3 leading-normal">Are you sure you wish to approve this artwork? This indicates you are ready for this item to be sent to print, if applicable.</p>
      <p>To request amends, please use the contact form at the bottom of the page.</p>
    </deletion-modal>
  </div>
</template>

<script>
import api, { v2Api } from '@/http'
import http from 'axios'
import { filter, isEmpty } from 'lodash'
import PrintOrderCustomField from './print-order-form/details/PrintOrderCustomField'
import PrintOrderDelivery from './print-order-form/details/PrintOrderDelivery'
import PrintOrderItemSummary from './print-order-form/details/PrintOrderItemSummary'
import { mapState, mapActions } from 'vuex'
import GaugeGraph from '@/components/Graphs/GaugeGraph'
import UploadDropzone from '@/components/UploadDropzone'
import DeletionModal from '@/components/Modals/DeletionModal'
export default {
  components: {
    PrintOrderCustomField,
    PrintOrderDelivery,
    PrintOrderItemSummary,
    GaugeGraph,
    UploadDropzone,
    DeletionModal
  },
  data () {
    return {
      itemId: null,
      loaded: false,
      loadingItem: false,
      updatingItem: false,
      officeAccess: true,
      orderItem: null,
      deliveryAddresses: [],
      deliveryAddress: null,
      deliveryAddressId: null,
      customFields: null,
      notes: null,
      savingInternalNote: false,
      internalNoteForm: {
        custom_print_order_item_id: null,
        body: '',
        show_studio: false,
        show_admin: false
      },
      reordering: false,
      approvingArtwork: false,
      sendingArtworkApproval: false
    }
  },
  computed: {
    ...mapState(['selectedOffice', 'profile']),
    attachmentsFileList: function () {
      if (!this.orderItem || !this.orderItem.attachments || this.orderItem.attachments.length === 0) {
        return ''
      }

      return this.orderItem.attachments.map((attachment) => attachment.filename).join(', ')
    },
    sourceMaterial () {
      if (!this.orderItem || !this.orderItem.attachments || this.orderItem.attachments.length === 0) {
        return []
      }
      return filter(this.orderItem.attachments, (item) => item.is_proof === false && item.is_print_pdf === false)
    },
    deliveryEditable () {
      return !this.orderItem.sent_to_print_at && this.orderItem.deleted_at === null
    },
    hasAdditionalContacts () {
      return this.orderItem.combined_notification_cc && this.orderItem.combined_notification_cc.length > 0
    },
    salesOrderDeliveryAddress () {
      if (!this.orderItem || !this.orderItem.sales_order) {
        return null
      }

      return this.orderItem.sales_order.delivery_address || null
    },
    artworkType () {
      if (this.orderItem.artwork_type_id === 1) {
        return 'Studio to create'
      }
      if (this.orderItem.artwork_type_id === 2) {
        return 'Agent to supply'
      }
      if (this.orderItem.artwork_type_id === 3) {
        return 'Reuse from previous order'
      }

      return 'None'
    },
    requiresProof () {
      return this.orderItem.sale_item.requires_proof !== false
    },
    checkOrderPermission () {
      return this.orderItem.office.id === this.selectedOffice.id
    }
  },
  watch: {
  },
  created () {
    this.itemId = this.$route.params.itemId ? this.$route.params.itemId : null
    this.loadOrderItem().then(() => {
      Promise.all([
        this.loadDeliveryAddresses(),
        this.getNotes(),
        this.loadCustomFields()
      ]).then(() => {
        this.loaded = true
      })
    })
  },
  methods: {
    isEmpty,
    ...mapActions([
      'displayToastNotification'
    ]),
    loadOrderItem () {
      if (!this.itemId) {
        return
      }

      this.loadingItem = true

      return v2Api.get(`/custom-print-order-items/${this.itemId}`).then((response) => {
        this.orderItem = response.data
        this.orderItem.uploads = []
        this.deliveryAddressId = this.orderItem.order.delivery_address_id
      }, (errors) => {
        this.errorBag.store(errors.response.data)
      }).finally(() => {
        this.loadingItem = false
      })
    },
    loadCustomFields () {
      v2Api.get(`sale-items/${this.orderItem.sale_item.id}/custom-field-items`).then((response) => {
        this.customFields = response.data
      })
    },
    loadDeliveryAddresses () {
      return api.get('delivery-addresses', {
        params: {
          office_id: this.selectedOffice.id
        }
      }).then((response) => {
        this.deliveryAddresses = response.data
      })
    },
    updateDeliveryAddress (id) {
      this.updatingItem = true
      v2Api.patch(`/custom-print-orders/${this.orderItem.order.id}`, {
        delivery_address_id: id
      }).then((response) => {
        this.deliveryAddressId = id
        this.updatingItem = false
      }, (errors) => {
        this.setErrorMessage(errors.response.data)
        this.updatingItem = false
      })
    },
    getNotes () {
      v2Api.get(`/custom-print-orders/${this.orderItem.order.id}/notes`, {
        params: {
          custom_print_order_item_id: this.itemId
        }
      }).then((response) => {
        this.notes = response.data
      })
    },
    addNote () {
      this.savingInternalNote = true
      this.internalNoteForm.custom_print_order_item_id = this.itemId
      v2Api.post(`/custom-print-orders/${this.orderItem.order.id}/notes`, this.internalNoteForm).then((response) => {
        this.notes.splice(0, 0, response.data)
        this.savingInternalNote = false
        this.internalNoteForm.body = ''
        this.internalNoteForm.show_office = true
      }, (errors) => {
        this.displayToastNotification({
          type: 'danger',
          icon: 'fa-exclamation-triangle',
          title: 'Error',
          info: errors.response.data.message
        })
        this.savingInternalNote = false
      })
    },
    checkIfPrivateNote (note) {
      return !note.show_admin && !note.show_studio
    },
    deleteNote (note) {
      v2Api.delete(`/custom-print-orders/${this.orderItem.order.id}/notes/${note.id}`).then(() => {
        this.notes.splice(this.notes.indexOf(note), 1)
      })
    },
    reOrder (item) {
      this.reordering = true
      v2Api.post(`/custom-print-order-items/${item.id}/re-order`)
        .then(response => {
          this.$router.push(`/print-order-form/saved-items/${response.data.id}`)
        }, (errors) => {
          this.displayToastNotification({
            type: 'danger',
            icon: 'fa-exclamation',
            title: item ? 'Error re-ordering item' : 'Error re-ordering items',
            info: errors.response.data.message
          })
        }).finally(() => {
          this.reordering = false
        })
    },
    uploadFiles (files) {
      for (let i = 0; i < files.length; i++) {
        this.uploadFile(files[i])
      }
    },
    async uploadFile (file) {
      // validate image type
      let isImage = false
      if (file.type === 'image/png' || file.type === 'image/jpeg' || file.type === 'image/tiff') {
        isImage = true
      }
      if (isImage && (file.size / 1000) < 500) {
        return this.displayToastNotification({
          type: 'danger',
          icon: 'fa-times-circle',
          title: 'Error',
          info: 'File uploads need a file size of at least 500kB to ensure image quality'
        })
      }
      let attachment = {
        filename: file.name,
        size: file.size,
        progress: 0,
        complete: false
      }
      this.orderItem.uploads.push(attachment)

      let filePath = `custom-print-orders/office/${this.selectedOffice.id}`

      const extension = file.name.split('.').pop()
      const response = await api.post('signed-upload', {
        'disk': 's3',
        'extension': extension,
        'path': filePath,
        'visibility': 'public-read',
        'content_type': file.type
      })

      let s3File = response.data.key
      let headers = response.data.headers
      if ('Host' in headers) {
        delete headers.Host
      }

      await http.put(response.data.url, file, {
        headers: headers,
        onUploadProgress: (progressEvent) => {
          attachment.progress = Math.ceil(progressEvent.loaded / progressEvent.total * 100)
        }
      })

      let data = {
        office_id: this.selectedOffice.id,
        file_path: s3File,
        file_name: file.name,
        is_proof: false,
        is_print_pdf: false
      }

      if (this.orderItem.id) {
        data.item_id = this.orderItem.id
      }

      v2Api.post('custom-print-order-attachments', data).then((response) => {
        attachment.complete = true
        this.orderItem.attachments.push(response.data.attachment)
        this.orderItem.uploads.splice(this.orderItem.uploads.indexOf(attachment), 1)
      }, (errors) => {
        this.orderItem.uploads.splice(this.orderItem.uploads.indexOf(attachment), 1)
        this.displayToastNotification({
          type: 'danger',
          icon: 'fa-times-circle',
          title: 'Error',
          info: errors.response.data.errors
        })
      })
    },
    deleteAsset (item, attachment) {
      if (confirm('Are you sure you would like to delete the file upload?')) {
        v2Api.delete(`custom-print-order-attachments/${attachment.id}`).then((response) => {
          item.attachments.splice(item.attachments.indexOf(attachment), 1)
        })
      }
    },
    approveArtwork () {
      if (this.sendingArtworkApproval) {
        return
      }

      this.sendingArtworkApproval = true
      v2Api.post(`/custom-print-order-items/${this.orderItem.id}/approve-artwork`).then((response) => {
        this.sendingArtworkApproval = false
        this.orderItem.approved_at = response.data.approved_at
        this.orderItem.completed_at = response.data.completed_at
        this.approvingArtwork = false
        this.displayToastNotification({
          type: 'success',
          icon: 'fa-check-circle',
          title: 'Success',
          info: 'Artwork approved'
        })
      }, () => {
        this.sendingArtworkApproval = false
        this.displayToastNotification({
          type: 'danger',
          icon: 'fa-times-circle',
          title: 'Error',
          info: 'Failed to approve artwork. Try again later.'
        })
      })
    },
    openUrl (url) {
      window.open(url, '_blank')
    }
  }
}
</script>

<style lang="sass">
#cpo-dropzone
  h3
    @apply font-normal text-sm
  label
    @apply bg-blue text-white
</style>
